/**
 * armAuthShellModalHost — arm the deferred modal host on the SIGNED-OUT shell.
 *
 * The shell mounts `ModalRoot` behind `if: s.root._modalHostReady` (app.js),
 * and the only writer of that key is the shared `bootApply`, which runs after
 * a SIGNED-IN `bootResolve`. A signed-out boot aborts before it, and index.js
 * mounts its own auth shell instead — so that shell's `ModalRoot` never
 * mounted. Sign-up and sign-in then continue in the SAME shell as a SPA (no
 * reboot), and every `confirmDialog` / `openModal` pushed its layer onto a host
 * with no node: no dialog, no message, no request (persona round 5, elena:
 * "Add to Workspace" on a fresh account's first built app did nothing).
 *
 * The arm keeps bootApply's rules: the host builds OUTSIDE the mount task, on
 * one macrotask after `create()` returns, so the login's first paint does not
 * pay for it. A macrotask, never a frame: a hidden tab has no frame to wait
 * for. A hidden tab also clamps its timers (1 s, then 1/min), so it arms on a
 * MessageChannel tick — a macrotask that is not throttled.
 *
 * Returns true when an arm was scheduled.
 */
export const armAuthShellModalHost = (
  shell,
  win = typeof window !== 'undefined' ? window : null
) => {
  const root = shell && shell.state ? shell.state.root || shell.state : null
  if (!root || typeof root.update !== 'function') return false
  if (root._modalHostReady) return false
  const arm = () => {
    if (root._modalHostReady) return
    root.update({ _modalHostReady: true }, { preventFetch: true })
  }
  const doc = win && win.document
  const Channel = win && win.MessageChannel
  if (doc && doc.visibilityState === 'hidden' && typeof Channel === 'function') {
    const ch = new Channel()
    ch.port1.onmessage = () => {
      ch.port1.close()
      arm()
    }
    ch.port2.postMessage(0)
  } else if (win && typeof win.setTimeout === 'function') win.setTimeout(arm, 0)
  else arm()
  return true
}
